import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useState, useEffect } from "react";
import RecomendedDNSmanager from "./ConnectDomainElements/RecomendedDNSmanager";
import CustomDNSmanager from "./ConnectDomainElements/CustomDNSmanager";
import DomainIntregationCongrats from "./ConnectDomainElements/DomainIntregationCongrats";
import { websiteActionEndPointLogic } from "../../../../../../logic/AssetManagement/Websites/websiteActionEndPointLogic";

interface CreateWebsiteModalProps {
  open: boolean;
  handleClose: () => void;
  websiteList: any;
  oauthTokenHeader: string;
  onDomainIntegrationRequest?: (data: any, domainRequests: any) => void;
  referenceURL: any;
  additionalInfo: any;
}

function ConnectDomainModal({
  open,
  handleClose,
  websiteList,
  oauthTokenHeader,
  onDomainIntegrationRequest,
  additionalInfo,
}: CreateWebsiteModalProps) {
  const Websites = websiteList?.map((web: any) => ({
    name: web?.site_name,
    shared_uid: web?.shared_uid,
  }));

  const isStagingOrDevelopmentEnv =
    import.meta.env.VITE_ENV === "staging" ||
    import.meta.env.VITE_ENV === "development";
  const [, setIsValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedWebsiteName, setSelectedWebsiteName] = useState(null) as any;
  const [, setSelectedWebsiteSharedUid] = useState(null) as any;
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showCongratsDialog, setShowCongratsDialog] = useState(false);
  const [urlError, setUrlError] = useState<string>("");
  const [inputUrl, setInputUrl] = useState<string>("");
  const [request, setRequest] = useState([]) as any;
  const [domainRequestData, setDomainRequestData] = useState([]) as any;

  useEffect(() => {
    const fetchDomainRequests = async () => {
      try {
        await websiteActionEndPointLogic.getDomainRequestsData(
          oauthTokenHeader,
          setDomainRequestData
        );
      } catch (e: any) {
        console.error(e);
      }
    };
    fetchDomainRequests();
  }, [oauthTokenHeader]);

  useEffect(() => {
    if (
      request?.data &&
      request?.data !== null &&
      request?.data !== undefined
    ) {
      onDomainIntegrationRequest?.(request.data, domainRequestData);
    }
  }, [request, onDomainIntegrationRequest, domainRequestData]);

  const isValidUrl = (url: string): boolean => {
    try {
      // Basic URL pattern
      const urlPattern =
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      return urlPattern.test(url);
    } catch (e) {
      return false;
    }
  };
  const refUrl = `${window.location.origin}/operations/website_actions?search=${additionalInfo?.site_name}`;
  // Request Domain Integration
  const requestDomainIntegration = () => {
    const isFormValid = validate();
    if (!isFormValid) return;

    const data = {
      task_structure_shared_uid: "b57c93b5aed3483e8ad0ed03d1510159",
      task_structure_shared_id: "b57c93b5aed3483e8ad0ed03d1510159",
      owner: isStagingOrDevelopmentEnv ? 5 : 6,
      assignee: isStagingOrDevelopmentEnv ? 5 : 6,
      reference_id: additionalInfo?.shared_uid,
      reference_url: refUrl,
      website_shared_uid: additionalInfo?.shared_uid,
      type: "Domain Integration",
      segment_type: "website",
      segment_value: additionalInfo?.shared_uid,
      data:
        selectedOption === "Your DNS Manager"
          ? {
              domain_url: selectedWebsiteName || "",
              integration_type: "DNS Records",
              integration_status: "Pending Configuration",
              txt_record_name: "",
              txt_record_value: "",
              cname_record_name: "",
              cname_record_value: "",
            }
          : {
              domain_url: selectedWebsiteName || "",
              integration_type: "DNS Manager",
              integration_status: "Pending Configuration",
              dns_nameservers: [],
            },
    };

    setLoading(true);

    if (oauthTokenHeader) {
      websiteActionEndPointLogic
        .requestANewDomainIntegration(oauthTokenHeader, setRequest, data)
        .then((response: any) => {
          websiteActionEndPointLogic.getDomainRequestsData(
            oauthTokenHeader,
            setDomainRequestData
          );
          setLoading(false);
          handleClose(); // Close the main dialog
          setSelectedOption(null);
          setSelectedWebsiteName(null);
          setSelectedWebsiteSharedUid(null);
          setInputUrl("");
          setShowCongratsDialog(true); // Show the success dialog
          setRequest(response.data);
        })
        .catch((error) => {
          setLoading(false);
          console.error(error, "Failed to request domain integration");
        })
        .finally(() => {
          setLoading(false);
          setSelectedOption(null);
          setSelectedWebsiteName(null);
          setSelectedWebsiteSharedUid(null);
          setInputUrl("");
          handleClose();
        });
    } else {
      setLoading(false);
      throw new Error("User doesn't have permission to perform this action");
    }
  };

  // Validate the form
  const validate = () => {
    if (!selectedWebsiteName || !selectedOption) {
      setIsValid(false);
      return false;
    }
    setIsValid(true);
    return true;
  };

  const handleCloseRequestModal = () => {
    setSelectedWebsiteName(null);
    setSelectedWebsiteSharedUid(null);
    setSelectedOption(null);
    setInputUrl("");
    handleClose();
  };

  const validateUrl = (url: string) => {
    if (!url) {
      setUrlError("URL is required");
      return false;
    }

    if (!isValidUrl(url)) {
      setUrlError("Please enter a valid URL");
      return false;
    }

    const urlExists = Websites.some(
      (website: any) => website.name.toLowerCase() === url.toLowerCase()
    );

    if (urlExists) {
      setUrlError("This URL already exists");
      return false;
    }

    setUrlError("");
    return true;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <Box>
            <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
              Connect Your Website to a Domain
            </DialogTitle>
            <DialogContent
              sx={{ padding: "0px 0px 16px 0px", color: "#000000DE" }}
            >
              <DialogContentText
                id="alert-dialog-description"
                sx={{ color: "#000000DE" }}
              >
                By connecting your domain, you'll ensure that visitors can reach
                your website through a personalized web address. This process
                involves a few simple steps to either use your current DNS
                manager or delegate it to our DNS manager for seamless
                integration. Please fill your website URL and choose the
                preferred Integration Option:
              </DialogContentText>
            </DialogContent>
            <Box>
              <TextField
                fullWidth
                required
                label="Website URL"
                color="secondary"
                size="small"
                value={inputUrl}
                error={!!urlError}
                helperText={urlError}
                onChange={(e) => {
                  const value = e.target.value;
                  setInputUrl(value);
                  setSelectedWebsiteName(value);
                  validateUrl(value);
                }}
                onBlur={() => validateUrl(inputUrl)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ marginTop: "16px" }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  marginTop: "8px",
                  color: "#00000099",
                  fontWeight: "400",
                }}
              >
                mandatory field.
              </Typography>
            </Box>
            <Box mt={1}>
              <Typography fontWeight={500}>Integration Options</Typography>
              <Grid container columnSpacing={8} mt={2}>
                <Grid item xs={6} md={6}>
                  <Stack flexDirection={"column"} gap={1} alignItems={"center"}>
                    <FormControlLabel
                      value="#"
                      control={<Radio color="secondary" />}
                      checked={selectedOption === "Your DNS Manager"}
                      onChange={() => setSelectedOption("Your DNS Manager")}
                      label="I'll use your DNS Manager (recommended)"
                    />
                  </Stack>
                  <Card sx={{ padding: "8px" }}>
                    <RecomendedDNSmanager />
                  </Card>
                </Grid>
                <Grid item xs={6} md={6}>
                  <Stack direction="row" gap={1} alignItems={"center"}>
                    <FormControlLabel
                      value="#"
                      control={<Radio color="secondary" />}
                      checked={selectedOption === "My DNS Manager"}
                      onChange={() => setSelectedOption("My DNS Manager")}
                      label="I'll use my own DNS Manager (custom)"
                    />
                  </Stack>
                  <Card sx={{ padding: "8px" }}>
                    <CustomDNSmanager />
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Stack
            flexDirection={"row"}
            justifyContent={"end"}
            marginTop={2}
            gap={1}
          >
            <Button color="secondary" onClick={handleCloseRequestModal}>
              Cancel
            </Button>
            <Button
              data-tracker="47"
              variant="contained"
              color="secondary"
              onClick={requestDomainIntegration}
              disabled={!selectedWebsiteName || !selectedOption}
            >
              <Stack flexDirection={"row"} gap={1}>
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Request Domain Integration"
                )}
              </Stack>
            </Button>
          </Stack>
        </Paper>
      </Dialog>

      {/* Success Dialog */}
      <DomainIntregationCongrats
        open={showCongratsDialog}
        handleClose={() => setShowCongratsDialog(false)}
      />
    </>
  );
}

export default ConnectDomainModal;
